/**
 * UUID validation utilities
 */

export const UUIDValidation = {
  /**
   * Validates if a string is a valid UUID v4 format
   */
  isValidUUID: (value: any): value is string => {
    if (typeof value !== 'string') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
  },

  /**
   * Validates and sanitizes UUID input
   */
  validateUUID: (value: any, fieldName: string = 'UUID'): string => {
    if (!UUIDValidation.isValidUUID(value)) {
      throw new Error(`Invalid ${fieldName}: expected UUID format, got "${value}" (${typeof value})`);
    }
    return value;
  },

  /**
   * Safely generates a new UUID
   */
  generateUUID: (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
};

/**
 * Database field validation for Supabase operations
 */
export const DatabaseValidation = {
  /**
   * Validates customer ID before database operations.
   * Uses a standard UUID shape check (any version) — not only RFC "v4" variant bits.
   */
  validateCustomerId: (customerId: any): string => {
    if (customerId == null) {
      throw new Error('Invalid Customer ID: expected UUID string, got null/undefined');
    }
    const s = String(customerId).trim();
    if (!s) {
      throw new Error('Invalid Customer ID: empty string');
    }
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) {
      throw new Error(`Invalid Customer ID: expected UUID, got "${customerId}"`);
    }
    return s;
  },

  /**
   * Validates user ID before database operations
   */
  validateUserId: (userId: any): string => {
    return UUIDValidation.validateUUID(userId, 'User ID');
  },

  /**
   * Safely extracts and validates UUID from objects
   */
  extractUUID: (obj: any, field: string): string | null => {
    const value = obj?.[field];
    return UUIDValidation.isValidUUID(value) ? value : null;
  }
};